import { screenToResource } from '~/constants/config/permissions';
import { useAuthStore } from '~/store/auth.store';

/**
 * Check if the current user has a specific global permission.
 * Super admins bypass all permission checks.
 *
 * Supports both screen codes ("organization_members") and resource:action format ("users:read").
 * Screen codes are mapped to a resource:read check — e.g., "organization_members" → "members:read".
 */
export function usePermission(permission: string): boolean {
  const user = useAuthStore((state) => state.user);
  if (!user) return false;
  if (user.isSuperAdmin) return true;
  return hasPermission(user.permissions, permission);
}

/**
 * Batch-check multiple global permissions.
 * Returns a record mapping each permission key to a boolean.
 */
export function usePermissions(permissions: string[]): Record<string, boolean> {
  const user = useAuthStore((state) => state.user);

  const result: Record<string, boolean> = {};
  for (const perm of permissions) {
    result[perm] = (user?.isSuperAdmin ?? false) || hasPermission(user?.permissions, perm);
  }
  return result;
}

/**
 * Check if the permission exists in the array.
 * Handles both exact matches and screen-code-to-resource:action resolution.
 */
function hasPermission(permissions: string[] | undefined, permission: string): boolean {
  if (!permissions) return false;

  // Direct match (e.g., "users:read" in permissions array)
  if (permissions.includes(permission)) return true;

  // Screen code match — check if "resource:read" permission exists (minimum for page access)
  const resource = screenToResource[permission];
  if (resource) {
    return permissions.includes(`${resource}:read`);
  }

  return false;
}
